import { CreateAttempt } from '@/common/types/session';
import {
  playTrickyWordAudio,
  playWordAudio,
  soundOutWord,
} from '@/common/utils/audio';
import {
  dealHeartWordsCards,
  sanitize,
  selectCorrectItem,
} from '@/common/utils/helpers';
import trackMixpanelEvent from '@/common/utils/mixpanel';
import {
  getWordBySpelling,
  getWordGraphemes,
} from '@/common/utils/scopeAndSequence';
import { EventBus } from '@/game/GameComponents/PhaserGame/EventBus';
import {
  GameInstance,
  getGeneralGameState,
  getHeartWords,
  getSession,
  getStudent,
} from '@/game/GameComponents/PhaserGame/GameInstance';
import { MinigameController } from '@/game/GameComponents/PhaserGame/MinigameControllers/MinigameController';
import { GeneralGameState } from '@/game/GameComponents/PhaserGame/Types/IGeneralGameState';
import { HeartWordsCard } from '@/game/GameComponents/PhaserGame/Types/IMinigameCardData';
import GAME_EVENTS from '@/game/GameComponents/PhaserGame/Utils/gameEventsDef';
import { waitForSeconds } from '@/game/GameComponents/PhaserGame/Utils/PhaserUtils';
import { calculateProgress } from '@/game/gameHelpers';
import StudentGameCard from '@/game/prefabs/StudentGame/StudentGameCard';
import Minigame from '@/game/scenes/Minigame';
import { v4 as uuidv4 } from 'uuid';
import { CardFeedback } from '../Types/MinigameTypes';

export class HeartWordsController extends MinigameController {
  override startRound() {
    const generalGameState: GeneralGameState = getGeneralGameState();
    const isRoundEnded = generalGameState.isRoundEnded;
    const trickyWordsArray = generalGameState.gameData.trickyWords;

    if (!trickyWordsArray?.length) {
      console.error('No tricky words found');
      return;
    }

    if (isRoundEnded) {
      GameInstance.instance.updateGeneralGameState({
        isRoundEnded: false,
      });
    }

    //todo set tricky word set
    GameInstance.instance.updateGeneralGameState({});
    this.handleTrickyWordDeal();
  }

  async handleTrickyWordDeal() {
    const state = getHeartWords();
    const trickyWordsArray = getGeneralGameState().gameData.trickyWords;
    const correctTrickyWordCount = state.correctHeartWordCount;
    const previousWord = state.correctCard?.word;

    if (!trickyWordsArray?.length) {
      console.error('No tricky words found');
      return;
    }
    const correctTrickyWord = selectCorrectItem(
      trickyWordsArray,
      correctTrickyWordCount,
      previousWord,
    );
    if (!correctTrickyWord) {
      console.error('No correct tricky words card found');
      return;
    }

    const correctTrickyWordsCard = {
      id: uuidv4(),
      word: correctTrickyWord,
      isCorrect: true,
    };

    const trickyWordsCards = dealHeartWordsCards(
      correctTrickyWordsCard,
      trickyWordsArray,
    );

    GameInstance.instance.updateHeartWords({
      dealtCards: trickyWordsCards,
      correctCard: correctTrickyWordsCard,
    });

    await waitForSeconds(this.minigame.time, 0.5);
    playTrickyWordAudio(
      correctTrickyWordsCard.word.spelling.toLowerCase() || '',
    );
  }

  override async checkMinigameAnswer() {
    const { minigame } = this;
    minigame.setBoardInteractive(false);
    const generalGameState = getGeneralGameState();
    const trickyWordsState = getHeartWords();

    const student = getStudent();
    if (!student.id) {
      console.error('No student found');
      return;
    }

    const correctCard = trickyWordsState.dealtCards.find(
      (card) => card.isCorrect,
    );
    if (!correctCard) {
      console.error('No correct answer found');
      return;
    }

    const currentActivityId = getSession().currentActivity?.id;
    if (!currentActivityId) {
      console.error('No activity ID found');
      return;
    }

    const attempt = this.minigame.getInput();
    if (!attempt.length) {
      console.error('No attempt word found');
      minigame.setBoardInteractive(true);
      return;
    }

    const attemptIsCorrect = attempt === correctCard?.word.spelling;

    const attemptData: CreateAttempt = {
      organisationId: student.organisationId,
      studentId: student.id,
      activityId: currentActivityId,
      activityName: 'trickywords',
      studentAnswer: attempt,
      correctAnswer: correctCard.word.spelling,
      correctAnswerId: correctCard.word.id,
      cardsPlayed: minigame
        .getBoardCards()
        .map((card) => sanitize(card.cardText) ?? ''),
      incorrectChoices: trickyWordsState.dealtCards
        .filter((card) => !card.isCorrect)
        .map((card) => card.word.spelling),
      control: this.minigame.getControlScheme(student.settings.controllerType),
      startedAt: getSession().currentAttemptStartedAt,
      endedAt: Date.now(),
    };
    GameInstance.instance.addAttempt(attemptData);

    // update accuracy
    GameInstance.instance.updateActivityAccuracy(attemptIsCorrect);

    if (attemptIsCorrect) {
      // Handling for correct choice
      const newCount =
        trickyWordsState.correctHeartWordCount[getWordBySpelling(attempt)!.id] +
          1 || 1;
      GameInstance.instance.updateHeartWords({
        correctHeartWordCount: {
          ...trickyWordsState.correctHeartWordCount,
          [getWordBySpelling(attempt)!.id]: newCount,
        },
      });
      const updatedTrickyWordsState = getHeartWords();
      const updatedProgress = calculateProgress(
        generalGameState,
        updatedTrickyWordsState,
        100,
      );

      GameInstance.instance.updateGeneralGameState({
        progress: updatedProgress,
      });

      await this.showFeedback(this.getCardFeedback(), { correctAnswer: true });
      EventBus.emit(GAME_EVENTS.MINIGAME_CORRECT_ANSWER, attemptData);
    } else {
      // Handling for incorrect choice
      GameInstance.instance.updateGeneralGameState({
        isShowingCorrectAnswer: false,
      });
      await this.showFeedback(this.getItemFeedback(), {
        correctAnswer: false,
        forceIndividualFeedback: true,
        forceReadout: true,
      });
      EventBus.emit(GAME_EVENTS.MINIGAME_INCORRECT_ANSWER, attemptData);
      this.minigame.newRound();
    }
  }

  override canShowArticulatoryGestures(): boolean {
    return false;
  }

  override canShowWordSegmentation(): boolean {
    return getStudent().settings?.wordSegmentationEnabled ?? false;
  }

  createCardList(minigameScene: Minigame): {
    dealtCards: StudentGameCard[];
    boardCards: StudentGameCard[];
  } {
    return {
      dealtCards: getHeartWords().dealtCards.map((card: HeartWordsCard) => {
        const newCard = new StudentGameCard(minigameScene, 0, 0);
        minigameScene.initializeNewMinigameCard(newCard, card, 'hand');
        return newCard;
      }),
      boardCards: [],
    };
  }

  override getMaxBoardCards(): number {
    return 1;
  }

  override async playInstructions() {
    const wordToBuild = getHeartWords().correctCard?.word;

    trackMixpanelEvent('Replay word button clicked', {
      word: wordToBuild?.spelling,
    });

    const audioDelay = getStudent().settings.audio.audioDelay;
    const wordSegmentationEnabled =
      getStudent().settings.wordSegmentationEnabled;

    if (!wordToBuild) {
      console.error('No word to build found');
      return;
    }

    const wordGraphemes = getWordGraphemes(wordToBuild);
    await playWordAudio(wordToBuild.spelling.toLowerCase());
    if (wordSegmentationEnabled) {
      await soundOutWord(
        Object.values(wordGraphemes),
        audioDelay,
        Object.values(wordGraphemes),
      );
      await playWordAudio(wordToBuild.spelling.toLowerCase());
    }
  }

  override getCardFeedback(): CardFeedback[] {
    const boardState = this.minigame.getBoardDropSite().layoutGroup!;

    const wordToBuild = getHeartWords().correctCard?.word;
    if (!wordToBuild) return [];

    const submittedCard = boardState.getMinigameCards()[0] as StudentGameCard;
    return [
      {
        card: submittedCard,
        isCorrect:
          submittedCard.cardData?.word?.spelling.toLowerCase() ===
          wordToBuild.spelling.toLowerCase(),
      },
    ];
  }

  override async handleIncorrectFeedbackComplete() {
    // Highlight the correct card.
    const answer = getHeartWords().correctCard;
    const handState = this.minigame.getHandDropSite().layoutGroup!;
    const student = getStudent();
    if (!student) {
      console.error('No student found');
      return;
    }

    const correctCard = handState
      .getMinigameCards()
      .find(
        (card) =>
          card.cardData?.word?.spelling.toLowerCase() ===
          answer?.word.spelling.toLowerCase(),
      );

    const word = correctCard?.cardData?.word;
    if (!word) {
      console.error('No grapheme found');
      return;
    }

    // Highlight the correct card
    if (correctCard) {
      await this.doCardFeedback({ card: correctCard, isCorrect: true }, false);
    }

    await waitForSeconds(this.minigame.time, 1);
  }

  override async readFinalAnswer() {}
}
